import { apiFetch } from './project.js';

/**
 * The admin page's calls (prd-deltas §22). Every change answers with `effect`, what it
 * did in words, which the page shows as it is (CLAUDE.md, agent first).
 */

export type Access = 'pending' | 'active' | 'blocked';

export interface AdminUser {
  id: string;
  email: string;
  name: string | null;
  avatarUrl: string | null;
  emailVerified: boolean;
  access: Access;
  admin: boolean;
  createdAt: string;
  lastLoginAt: string | null;
  accessChangedAt: string | null;
  /** When they asked to be let in, and what they said; null if they have not asked. */
  requestedAt: string | null;
  requestNote: string | null;
  projects: number;
  runs: number;
  lifts: number;
}

export interface Invite {
  email: string;
  note: string | null;
  invitedBy: string | null;
  createdAt: string;
  acceptedAt: string | null;
}

export async function fetchAccess(signal?: AbortSignal): Promise<{ users: AdminUser[]; invites: Invite[] }> {
  const response = await apiFetch('/api/admin/access', { signal: signal ?? null, headers: { accept: 'application/json' } });
  if (!response.ok) throw new Error(await messageOf(response));
  return (await response.json()) as { users: AdminUser[]; invites: Invite[] };
}

export const setUserAccess = (id: string, access: Access): Promise<string> =>
  change(`/api/admin/users/${encodeURIComponent(id)}/access`, 'POST', { access });

export const invite = (email: string, note: string): Promise<string> =>
  change('/api/admin/invites', 'POST', { email, note });

export const withdrawInvite = (email: string): Promise<string> =>
  change(`/api/admin/invites/${encodeURIComponent(email)}`, 'DELETE');

/** One of Civil's own prompts (prd-deltas §31), as the admin page shows it. */
export interface AdminPrompt {
  name: string;
  title: string;
  area: string;
  description: string;
  /** The text in the runner's code. */
  default: string;
  /** The text in force: the latest edit, or the default. */
  text: string;
  /** Placeholders the code fills, which an edit must keep. */
  fields: string[];
  /** The word budget the tests hold the default to. */
  budget?: number;
  /** Who made the edit in force, when, and why; null while the default is in force. */
  edited: { by: string | null; at: string; note: string | null } | null;
}

export interface PromptSave {
  text: string | null;
  note: string | null;
  editedByEmail: string | null;
  at: string;
}

export async function fetchPrompts(signal?: AbortSignal): Promise<{ prompts: AdminPrompt[]; takesEffectWithinSeconds: number }> {
  const response = await apiFetch('/api/admin/prompts', { signal: signal ?? null, headers: { accept: 'application/json' } });
  if (!response.ok) throw new Error(await messageOf(response));
  return (await response.json()) as { prompts: AdminPrompt[]; takesEffectWithinSeconds: number };
}

export async function fetchPromptHistory(name: string, signal?: AbortSignal): Promise<PromptSave[]> {
  const response = await apiFetch(`/api/admin/prompts/${encodeURIComponent(name)}/history`, { signal: signal ?? null, headers: { accept: 'application/json' } });
  if (!response.ok) throw new Error(await messageOf(response));
  return ((await response.json()) as { history: PromptSave[] }).history;
}

export const savePrompt = (name: string, text: string, note: string): Promise<string> =>
  change(`/api/admin/prompts/${encodeURIComponent(name)}`, 'PUT', { text, note });

export const resetPrompt = (name: string): Promise<string> =>
  change(`/api/admin/prompts/${encodeURIComponent(name)}`, 'DELETE');

/** Placeholders a template's text holds ({name}, not {{escaped}}), for the editor's own check. */
export function placeholdersIn(text: string): string[] {
  const found = new Set<string>();
  for (const m of text.replace(/\{\{|\}\}/g, '').matchAll(/\{([A-Za-z_][A-Za-z0-9_]*)[^{}]*\}/g)) found.add(m[1]!);
  return [...found];
}

export const wordCount = (text: string): number => (text.trim() ? text.trim().split(/\s+/).length : 0);

/** Resolves to the server's effect; rejects with its message, which is written for people. */
async function change(url: string, method: string, body?: unknown): Promise<string> {
  const response = await apiFetch(url, {
    method,
    headers: body === undefined ? {} : { 'content-type': 'application/json' },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  if (!response.ok) throw new Error(await messageOf(response));
  return ((await response.json()) as { effect: string }).effect;
}

async function messageOf(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as { message?: string };
    return body.message ?? `The server answered ${response.status}.`;
  } catch {
    return `The server answered ${response.status}.`;
  }
}
